<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { landingPages } from '../../content/landingPages'
import ToolCard from '../ToolCard.vue'
import { workbenchToolsMeta } from '../workbenchTools'
import { TASK_TYPES } from '../taskCatalog'

type ToolItem = {
  id: string
  title: string
  desc: string
  iconId: string
  href: string
  group: 'video' | 'image'
}

function withBase(p: string) {
  return `${import.meta.env.BASE_URL || '/'}${String(p || '').replace(/^\//, '')}`
}

const byId = Object.fromEntries(landingPages.map((p) => [p.id, p]))
const route = useRoute()
const router = useRouter()
const VideoWatermarkRemovalPage = defineAsyncComponent(() => import('../../views/VideoWatermarkRemovalPage.vue'))
const VideoSubtitleRemovalPage = defineAsyncComponent(() => import('../../views/VideoSubtitleRemovalPage.vue'))
const VideoObjectRemovalPage = defineAsyncComponent(() => import('../../views/VideoObjectRemovalPage.vue'))
const VideoUpscalePage = defineAsyncComponent(() => import('../../views/VideoUpscalePage.vue'))
const ImageObjectRemovalPage = defineAsyncComponent(() => import('../../views/ImageObjectRemovalPage.vue'))

const subtitleDemoVideoUrl = computed(() => {
  const v = (byId as any)['video-subtitle-removal']?.demoVideoUrl
  return String(v || '').trim()
})

const embeddedToolId = computed(() => {
  const q = route.query as any
  const embed = String(q?.embed || '') === '1'
  const id = String(q?.tool || '').trim()
  return embed ? id : ''
})

const isEmbeddedWatermark = computed(() => embeddedToolId.value === TASK_TYPES.removeWatermark_vid)
const isEmbeddedSubtitle = computed(() => embeddedToolId.value === TASK_TYPES.removeText_vid)
const isEmbeddedObjectRemoval = computed(() => embeddedToolId.value === TASK_TYPES.eraseObject_vid)
const isEmbeddedUpscale = computed(() => embeddedToolId.value === TASK_TYPES.upscale_vid)
const isEmbeddedImageObjectRemoval = computed(() => embeddedToolId.value === TASK_TYPES.remove_pic)

function fallbackHref(landingId: string) {
  const k = String(landingId || '').trim()
  if (!k) return withBase('/')
  return withBase(`/${k}-app/`)
}

const tools = computed<ToolItem[]>(() => {
  return workbenchToolsMeta.map((t) => {
    const landing = (byId as any)[t.landingId]
    const routePath = landing?.routePath
    return {
      id: t.id,
      group: t.group,
      title: t.title,
      desc: t.desc,
      iconId: t.icon,
      href: routePath ? withBase(routePath) : fallbackHref(t.landingId),
    }
  })
})

const comingSoonIds = new Set<string>([
  TASK_TYPES.translate_vid,
  TASK_TYPES.changeBG_pic,
  TASK_TYPES.upscale_pic,
])

function isComingSoon(t: ToolItem | null) {
  if (!t) return false
  return comingSoonIds.has(t.id)
}

function goUse(t: ToolItem) {
  if (isComingSoon(t)) return
  if (
    t.id === TASK_TYPES.removeWatermark_vid ||
    t.id === TASK_TYPES.removeText_vid ||
    t.id === TASK_TYPES.eraseObject_vid ||
    t.id === TASK_TYPES.upscale_vid ||
    t.id === TASK_TYPES.remove_pic
  ) {
    router.replace({ query: { ...(route.query as any), tool: t.id, embed: '1' } })
    return
  }
  window.open(t.href, '_blank')
}

const videoRank: Record<string, number> = {
  [TASK_TYPES.removeWatermark_vid]: 0,
  [TASK_TYPES.removeText_vid]: 1,
  [TASK_TYPES.eraseObject_vid]: 2,
  [TASK_TYPES.upscale_vid]: 3,
  [TASK_TYPES.translate_vid]: 4,
}
const videoTools = computed(() =>
  tools.value
    .filter((t) => t.group === 'video')
    .slice()
    .sort((a, b) => (videoRank[a.id] ?? 99) - (videoRank[b.id] ?? 99)),
)
const imageTools = computed(() => tools.value.filter((t) => t.group === 'image'))
</script>

<template>
  <div>
    <div v-if="isEmbeddedWatermark">
      <component :is="VideoWatermarkRemovalPage" :embed="true" />
    </div>

    <div v-else-if="isEmbeddedSubtitle">
      <component :is="VideoSubtitleRemovalPage" :embed="true" :demoVideoUrl="subtitleDemoVideoUrl" />
    </div>

    <div v-else-if="isEmbeddedObjectRemoval">
      <component :is="VideoObjectRemovalPage" :embed="true" />
    </div>

    <div v-else-if="isEmbeddedUpscale">
      <component :is="VideoUpscalePage" :embed="true" />
    </div>

    <div v-else-if="isEmbeddedImageObjectRemoval">
      <component :is="ImageObjectRemovalPage" :embed="true" />
    </div>

    <template v-else>
    <section>
      <h2 class="text-2xl font-extrabold text-on-surface font-manrope">视频工具</h2>
      <div class="grid grid-cols-[repeat(auto-fit,minmax(300px,600px))] gap-6 mt-6 justify-start items-stretch">
        <ToolCard
          v-for="t in videoTools"
          :key="t.id"
          :title="t.title"
          :desc="t.desc"
          :iconId="t.iconId"
          :ctaLabel="isComingSoon(t) ? '即将上线' : '去使用'"
          :ctaDisabled="isComingSoon(t)"
          :disabled="isComingSoon(t)"
          @go="goUse(t)"
        />
      </div>
    </section>

    <section class="mt-12">
      <h2 class="text-2xl font-extrabold text-on-surface font-manrope">图片工具</h2>
      <div class="grid grid-cols-[repeat(auto-fit,minmax(300px,600px))] gap-6 mt-6 justify-start items-stretch">
        <ToolCard
          v-for="t in imageTools"
          :key="t.id"
          :title="t.title"
          :desc="t.desc"
          :iconId="t.iconId"
          :ctaLabel="isComingSoon(t) ? '即将上线' : '去使用'"
          :ctaDisabled="isComingSoon(t)"
          :disabled="isComingSoon(t)"
          @go="goUse(t)"
        />
      </div>
    </section>
    </template>
  </div>
</template>
